import { Button, Toolbar } from '@mui/material';
import Box from '@mui/material/Box';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useRef } from 'react';
import BackIcon from 'src/components/Icons/BackIcon';
import useDeviceSize from 'src/helpers/hooks/useDeviceSize';
import { useMount } from 'src/helpers/hooks/useMount';
import TheGuarantorsLogoLg from '../assets/TheGuarantors_Header_Logo.svg';

const ToolbarBoxStyled = styled(Box, {
  shouldForwardProp: (propName: string) => propName !== 'isStickToTop',
})<HeaderV2Props>(({ theme, isStickToTop }) => ({
  padding: '20px 20px 40px',
  borderColor: 'white',
  marginTop: 0,
  left: 0,
  top: 0,
  width: 'stretch',

  [theme.breakpoints.down('lg')]: {
    padding: isStickToTop ? '0' : '10px 0 0',
  },

  [theme.breakpoints.down('sm')]: {
    position: 'relative',
    borderColor: theme.palette.background.default,
    left: '0.5rem',
    top: '8px',
    width: 'calc(100% - 1rem)',
  },
}));

const ToolbarStyled = styled(Toolbar)(({ theme }) => ({
  background: 'white',
  justifyContent: 'center',
  height: '80px',
  borderRadius: '8px',
  boxShadow: '4px 16px 32px rgba(0, 0, 0, 0.08)',
  div: {
    img: {
      height: '24px',
    },
  },

  [theme.breakpoints.down('lg')]: {
    height: '52px',
    minHeight: '52px',
    borderRadius: 0,
    boxShadow: '4px 16px 32px 0 rgba(29, 29, 29, 0.08)',

    div: {
      img: {
        height: '20px',
      },
    },
  },

  [theme.breakpoints.down('sm')]: {
    height: '52px',
    boxShadow: '4px 16px 32px 0 rgba(29, 29, 29, 0.08)',
  },
}));

const LeftSectionStyled = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
});

const CenterSectionStyled = styled(LeftSectionStyled)({
  flex: 2,
  justifyContent: 'center',
});

const RightSectionStyled = styled(LeftSectionStyled)({});

export type HeaderV2Props = {
  isStickToTop?: boolean;
  backButtonAction?: (() => void) | null;
};

function useStickyHeader() {
  const headerRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();

  const tabMaxBreakpoint = theme.breakpoints.values.lg + 1;
  const isTablet = useMediaQuery(theme.breakpoints.down(tabMaxBreakpoint));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useMount(() => {
    const header = headerRef.current;
    const toolbar = toolbarRef.current;

    if (!header || !toolbar) {
      return;
    }

    const toolbarHeight = toolbar.getBoundingClientRect().height;
    const headerStyles = window.getComputedStyle(header);
    const headerPaddingTop = headerStyles.paddingTop;
    const headerPaddingLeft = headerStyles.paddingLeft;
    const headerPaddingRight = headerStyles.paddingRight;
    const headerWidth = headerStyles.width;

    if (isTablet) {
      toolbar.style.boxShadow = 'none';
    }

    function handleScroll() {
      if (!header || !toolbar) {
        return;
      }

      const headerTop = header.getBoundingClientRect().top;
      const isSticky = headerTop <= 0;

      if (isSticky) {
        header.style.height = `${toolbarHeight}px`;
        toolbar.style.position = 'fixed';
        toolbar.style.top = headerPaddingTop;
        toolbar.style.left = isMobile ? '50%' : headerPaddingLeft;
        toolbar.style.right = headerPaddingRight;
        toolbar.style.zIndex = '100';
        toolbar.style.height = `${toolbarHeight}px`;

        if (isMobile) {
          toolbar.style.boxSizing = 'border-box';
          toolbar.style.width = headerWidth;
          toolbar.style.transform = 'translateX(-50%)';
        }

        return;
      }

      toolbar.style.position = 'static';
      toolbar.style.height = 'auto';
      header.style.height = 'auto';

      if (isMobile) {
        toolbar.style.width = 'auto';
        toolbar.style.maxWidth = 'auto';
        toolbar.style.transform = 'none';
        toolbar.style.left = 'auto';
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  });

  return { headerRef, toolbarRef };
}

const HeaderV2 = ({ isStickToTop, backButtonAction }: HeaderV2Props) => {
  const { headerRef, toolbarRef } = useStickyHeader();
  const { desktop } = useDeviceSize();

  return (
    <ToolbarBoxStyled ref={headerRef} isStickToTop={isStickToTop}>
      <ToolbarStyled ref={toolbarRef}>
        <LeftSectionStyled>
          {backButtonAction && (
            <Button
              size="small"
              variant="text"
              startIcon={<BackIcon size={desktop ? 40 : 30} />}
              onClick={backButtonAction}
              sx={{
                padding: '2px 4px !important',
              }}
            >
              {desktop && <span style={{ marginLeft: '8px' }}>Back</span>}
            </Button>
          )}
        </LeftSectionStyled>

        <CenterSectionStyled>
          <img alt="" src={TheGuarantorsLogoLg} />
        </CenterSectionStyled>

        <RightSectionStyled />
      </ToolbarStyled>
    </ToolbarBoxStyled>
  );
};

export default HeaderV2;
